<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EmojiInput from 'shared/components/emoji/EmojiInput.vue';

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
    EmojiInput,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      showEditModal: false,
      editContent: '',
      showEmojiPicker: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
    messageInboxId() {
      return this.message.inbox_id ?? this.message.inboxId;
    },
    messageInbox() {
      const getInbox = this.$store.getters['inboxes/getInbox'];
      return typeof getInbox === 'function'
        ? getInbox(this.messageInboxId)
        : null;
    },
    // Inbox conectada via Uazapi: e o unico canal onde a edicao propaga pro
    // WhatsApp. A fonte de verdade e o booleano `uazapi`, que o backend serializa
    // para todo agente. A regex antiga no webhook_url fica como fallback porque o
    // payload de inbox mora no IndexedDB (CacheEnabledApiClient) e nao revalida so
    // porque a serializacao mudou: enquanto o cache velho nao expira, so ele tem a
    // resposta. E, depois do gate, webhook_url so desce para administrador, entao o
    // fallback morre sozinho quando o cache do agente virar.
    isUazapiInbox() {
      const uazapiFlag = this.messageInbox?.uazapi;
      if (uazapiFlag === undefined || uazapiFlag === null) {
        const url = this.messageInbox?.webhook_url || '';
        return /\/\/[^/]*\.uazapi\.com\/chatwoot\/webhook\//i.test(url);
      }
      return Boolean(uazapiFlag);
    },
    isOutgoingMessage() {
      const type = this.message.message_type ?? this.message.messageType;
      return type === 1 || type === 'outgoing';
    },
    // WhatsApp so deixa editar dentro de ~15 min do envio.
    withinEditWindow() {
      const ts = Number(this.message.created_at ?? this.message.createdAt);
      if (!ts) return false;
      return Date.now() / 1000 - ts < 15 * 60;
    },
    // Nao exigimos source_id: mensagem digitada na plataforma nao tem, e o
    // backend descobre o id no WhatsApp (via track_id da Uazapi). Se nao achar,
    // o atendente e avisado na propria conversa.
    isMessageEditable() {
      return (
        this.isUazapiInbox && this.isOutgoingMessage && this.withinEditWindow
      );
    },
    // A uazapi so aceita reagir a mensagem de OUTRO (incoming), dentro de 7 dias.
    isMessageReactable() {
      const ts = Number(this.message.created_at ?? this.message.createdAt);
      if (!ts) return false;
      const withinWindow = Date.now() / 1000 - ts < 7 * 24 * 60 * 60;
      return this.isUazapiInbox && !this.isOutgoingMessage && withinWindow;
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.showEmojiPicker = false;
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      });
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    openEditModal() {
      this.editContent = this.plainTextContent;
      this.handleClose();
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    async confirmEdit() {
      const content = (this.editContent || '').trim();
      if (!content) return;
      try {
        await this.$store.dispatch('editMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          content,
        });
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.EDIT_SUCCESS'));
        this.closeEditModal();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.EDIT_FAIL'));
      }
    },
    async handleReact(emoji) {
      this.showEmojiPicker = false;
      try {
        await this.$store.dispatch('reactToMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          emoji,
        });
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.REACT_SUCCESS'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.CONTEXT_MENU.REACT_FAIL'));
      }
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal && enabledOptions['delete']"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <!-- Edit message (Helevor/Uazapi) -->
    <woot-modal
      v-if="showEditModal"
      v-model:show="showEditModal"
      :on-close="closeEditModal"
    >
      <div class="flex flex-col p-8">
        <woot-modal-header
          :header-title="$t('CONVERSATION.CONTEXT_MENU.EDIT_MODAL.TITLE')"
          :header-content="$t('CONVERSATION.CONTEXT_MENU.EDIT_MODAL.DESC')"
        />
        <form class="w-full mt-4" @submit.prevent="confirmEdit">
          <textarea
            v-model="editContent"
            rows="4"
            class="w-full p-2 border rounded-md resize-none border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-n-brand"
            :placeholder="
              $t('CONVERSATION.CONTEXT_MENU.EDIT_MODAL.PLACEHOLDER')
            "
          />
          <div class="flex flex-row justify-end w-full gap-2 mt-4">
            <NextButton
              faded
              slate
              type="button"
              :label="$t('CONVERSATION.CONTEXT_MENU.EDIT_MODAL.CANCEL')"
              @click.stop="closeEditModal"
            />
            <NextButton
              type="submit"
              :label="$t('CONVERSATION.CONTEXT_MENU.EDIT_MODAL.SAVE')"
              :disabled="!editContent.trim()"
            />
          </div>
        </form>
      </div>
    </woot-modal>
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <MenuItem
          v-if="enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <MenuItem
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <MenuItem
          v-if="isMessageReactable"
          :option="{
            icon: 'emoji',
            label: $t('CONVERSATION.CONTEXT_MENU.REACT'),
          }"
          variant="icon"
          @click.stop="showEmojiPicker = !showEmojiPicker"
        />
        <MenuItem
          v-if="isMessageEditable"
          :option="{
            icon: 'edit',
            label: $t('CONVERSATION.CONTEXT_MENU.EDIT'),
          }"
          variant="icon"
          @click.stop="openEditModal"
        />
        <MenuItem
          v-if="enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click.stop="handleTranslate"
        />
        <hr />
        <MenuItem
          v-if="enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr v-if="enabledOptions['delete']" />
        <MenuItem
          v-if="enabledOptions['delete']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal"
        />
      </div>
      <!-- O picker foca o proprio campo de busca ao montar; quem garante que isso
           nao fecha o menu e o guard de focusout em ContextMenu.vue. -->
      <EmojiInput
        v-if="showEmojiPicker"
        :on-click="handleReact"
        class="reaction-emoji-picker"
      />
    </ContextMenu>
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-n-background shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-n-strong;
  }
}

.context-menu--delete-modal {
  ::v-deep {
    .modal-container {
      @apply max-w-[30rem];

      h2 {
        @apply font-medium text-base;
      }
    }
  }
}

// EmojiInput.vue posiciona o dialog com `absolute right-0 -top-[95px]`,
// calibrado pra abrir acima do composer. Dentro do menu de contexto (que ja e
// um popup fixed/clamped ao viewport em ContextMenu.vue) isso rendia fora da
// tela. Em vez de lutar com a posicao absoluta, colocamos o picker no fluxo
// normal (abaixo da lista de opcoes): o proprio ContextMenu.vue reobserva o
// tamanho do menu (ResizeObserver) e reposiciona tudo pra caber na tela.
.reaction-emoji-picker {
  position: static !important;
  margin-top: 0.25rem !important;

  // A seta que apontava pro composer nao faz sentido aqui.
  &::before {
    display: none !important;
  }
}
</style>
